import { z } from "zod";
import { Form } from "@/components/ui/form";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { CheckboxField, DatePickerField, InputField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { Diff } from "@/features/FormCard/Diff";
import {
  dateSchema,
  parseToDateValue,
  parseYes,
} from "@/features/FormCard/utils";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { useEditForm } from "@/features/Edit/hooks";
import { Tables } from "@/gen/tableNames/tables";
import { fieldTranslations } from "./translations";
import { useStrategyCurrency } from "./useStrategyCurrency";

const FormSchema = z.object({
  strategy_from_id: z.coerce.number().nullable().optional(),
  strategy_to_id: z.coerce.number().nullable().optional(),
  quantity: z.coerce.number().nullable().optional(),
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),
  weight: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  unit_price: z.coerce.number().nullable().optional(),
  au_pair: z.coerce.number().nullable().optional(),
  percent_unit_price: z.coerce.number().nullable().optional(),
  acquisition_cost: z.coerce.number().nullable().optional(),
  premium_discount: z.coerce.number().nullable().optional(),
  impairment_losses: z.coerce.number().nullable().optional(),
  accrued_interests: z.coerce.number().nullable().optional(),
  net_book_value: z.coerce.number().nullable().optional(),
  tax_basis_value: z.coerce.number().nullable().optional(),
  pledged_asset: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  cic_code: z.string().nullable().optional(),
  // Not rendered as inputs, but must stay declared here so Zod doesn't strip
  // them from the cached item data that <TechnicalInfoCard> reads.
  time_serie_file_id: z.coerce.number().nullable().optional(),
  m2_time_serie_file_id: z.coerce.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
const formSchema = FormSchema.keyof().enum;

export function M2PositionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation([Tables.M2_POSITIONS, "tables"]);

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    handleDelete,
  } = useEditForm({ id, client, table_name, FormSchema });

  const strategyFromId = form.watch("strategy_from_id");
  const strategyToId = form.watch("strategy_to_id");
  const strategyFromCurrency = useStrategyCurrency(strategyFromId);
  const strategyToCurrency = useStrategyCurrency(strategyToId);

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Position",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.strategy_from_id}
                    label={t(`columns.strategy_from_id`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Stratégie (depuis)",
                    })}
                    control={form.control}
                    endContent={
                      strategyFromCurrency ? (
                        <span className="text-xs text-default-400 whitespace-nowrap">
                          {strategyFromCurrency}
                        </span>
                      ) : undefined
                    }
                  />
                  <InputField
                    type="number"
                    name={formSchema.strategy_to_id}
                    label={t(`columns.strategy_to_id`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Stratégie (vers)",
                    })}
                    control={form.control}
                    endContent={
                      strategyToCurrency ? (
                        <span className="text-xs text-default-400 whitespace-nowrap">
                          {strategyToCurrency}
                        </span>
                      ) : undefined
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchema.date}
                    label={t(`columns.date`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.quantity}
                    label={t(`columns.quantity`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Quantité",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.weight}
                    label={t(`columns.weight`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Poids",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.unit_price}
                    label={t(`columns.unit_price`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Prix unitaire",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.market_value_local_ccy}
                    label={t(`columns.market_value_local_ccy`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Valeur marché (devise locale)",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.market_value_ptf_ccy}
                    label={t(`columns.market_value_ptf_ccy`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Valeur marché (devise ptf)",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                  <InputField
                    type="number"
                    name={formSchema.au_pair}
                    label={t(`columns.au_pair`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Au pair",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name={formSchema.pledged_asset}
                    label={t(`columns.pledged_asset`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Actif nanti",
                    })}
                    control={form.control}
                    className="pb-2"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.percent_unit_price}
                    label={t(`columns.percent_unit_price`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "% prix unitaire",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.acquisition_cost}
                    label={t(`columns.acquisition_cost`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Coût d'acquisition",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.premium_discount}
                    label={t(`columns.premium_discount`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Prime/décote",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.impairment_losses}
                    label={t(`columns.impairment_losses`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Pertes de valeur",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.accrued_interests}
                    label={t(`columns.accrued_interests`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Intérêts courus",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchema.net_book_value}
                    label={t(`columns.net_book_value`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Valeur nette comptable",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchema.tax_basis_value}
                    label={t(`columns.tax_basis_value`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Valeur fiscale",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="text"
                    name={formSchema.cic_code}
                    label={t(`columns.cic_code`, {
                      ns: Tables.M2_POSITIONS,
                      defaultValue: "Code CIC",
                    })}
                    control={form.control}
                  />
                </div>

              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? Diff(changedFields, [], fieldTranslations, table_name)
            : t("no_changes")}
        </ConfirmationModal>

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
